/**
 * Registry for collecting and deduplicating JSON Schemas.
 * Block emitters register schemas by name, receiving $ref pointers.
 * Eliminates post-processing deduplication in schemaUtils.
 *
 * Unlike ForeignKeyRegistry (which uses callbacks for lazy property expansion),
 * JsonSchemaRegistry provides immediate registration and deduplication at registration time.
 */
class JsonSchemaRegistry {
    constructor() {
        this.schemas = new Map(); // name → schema
        this.canonicalMap = new Map(); // canonical form → name (for dedup)
    }

    /**
     * Register a schema by name, returning a $ref pointer.
     * If an identical schema exists, returns ref to existing.
     * @param {string} name - Schema name (e.g., 'Core.User', 'Address')
     * @param {object} schema - JSON Schema object
     * @returns {{$ref: string}} - Reference to the registered schema
     */
    register = (name, schema) => {
        const canonical = this._canonicalize(schema);

        // Check for duplicate shape
        if (this.canonicalMap.has(canonical)) {
            const existingName = this.canonicalMap.get(canonical);
            return { $ref: `#/$defs/${existingName}` };
        }

        // Handle name collision with different schema
        const finalName = this._uniqueName(name);
        this.schemas.set(finalName, schema);
        this.canonicalMap.set(canonical, finalName);

        return { $ref: `#/$defs/${finalName}` };
    };

    /**
     * Register a namespace.relation schema (convenience method).
     * @param {string} namespace - Namespace name (e.g., 'Core')
     * @param {string} relation - Relation name (e.g., 'User')
     * @param {object} schema - JSON Schema object
     * @returns {{$ref: string}} - Reference to the registered schema
     */
    registerRelation = (namespace, relation, schema) => {
        return this.register(`${namespace}.${relation}`, schema);
    };

    /**
     * Get all registered schemas as $defs object.
     * @returns {object} - Object suitable for JSON Schema $defs
     */
    toDefs = () => {
        return Object.fromEntries(this.schemas);
    };

    /**
     * Check if a schema name is already registered.
     * @param {string} name - Schema name to check
     * @returns {boolean}
     */
    has = name => {
        return this.schemas.has(name);
    };

    /**
     * Get a registered schema by name.
     * @param {string} name - Schema name
     * @returns {object|undefined}
     */
    get = name => {
        return this.schemas.get(name);
    };

    /**
     * Get the number of registered schemas.
     * @returns {number}
     */
    get size() {
        return this.schemas.size;
    }

    /**
     * Create canonical form for semantic comparison.
     * Strips metadata fields that don't affect schema identity.
     * @param {object} schema - JSON Schema to canonicalize
     * @returns {string} - Stringified canonical form
     * @private
     */
    _canonicalize = schema => {
        const prune = obj => {
            if (!obj || typeof obj !== 'object') return obj;
            if (Array.isArray(obj)) return obj.map(prune);
            const out = {};
            Object.keys(obj)
                .sort()
                .forEach(k => {
                    // Skip metadata fields that don't affect schema identity
                    if (['title', 'description', 'default', 'readOnly', 'writeOnly'].includes(k)) return;
                    // Preserve x-namespace and x-relation for foreign key differentiation
                    if (k.startsWith('x-') && k !== 'x-namespace' && k !== 'x-relation') return;
                    out[k] = prune(obj[k]);
                });
            return out;
        };
        return JSON.stringify(prune(schema));
    };

    /**
     * Generate unique name if collision occurs.
     * @param {string} base - Base name to use
     * @returns {string} - Unique name (base or base__N)
     * @private
     */
    _uniqueName = base => {
        if (!this.schemas.has(base)) return base;
        let i = 2;
        while (this.schemas.has(`${base}__${i}`)) {
            i++;
        }
        return `${base}__${i}`;
    };
}

module.exports = JsonSchemaRegistry;
